import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import ResourceUpload from "./FileUploadOrDrag";
import { Modal, Button } from "react-bootstrap";
import ManagementHeader from "../ManagementHeader";
import { useUserStore } from "context/useUserStore";
import SpecificAPI from "services/SpecificApi";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";
import DeleteModal from "../DeleteModal";
import ResourcePreview from "./ResourcePreview";
import localization from "services/localization";
import { ResourceTable } from "./ResourceTable";
import { TooltipProvider } from "components/ui/tooltip_v2";

export default function ResourceManager() {
  const { departmentId, moduleId } = useParams();

  const [showModal, setShowModal] = useState(false);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemName, setItemName] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [itemToView, setItemToView] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const { appUser, userData } = useUserStore();

  useEffect(() => {
    if (!appUser?.id) return;
    if (appUser?.role == "root") {
      getAllUsers();
    }
    getResources(appUser.id);
  }, [appUser]);

  const getResources = async (userId) => {
    try {
      setLoading(true);

      // const [userResources, publicResources] = await Promise.all([
      //   SpecificAPI.getAllResources(
      //     { user_id: userId },
      //     userData?.selected_school_id,
      //   ),
      //   SpecificAPI.getAllResources(
      //     { visibility_scope: "public" },
      //     userData?.selected_school_id,
      //   ),
      // ]);
      const resources = await SpecificAPI.getAllResources(
        { enabled: true },
        userData?.selected_school_id,
      );
      // const merged = [...userResources, ...publicResources];

      // const uniqueResources = Array.from(
      //   new Map(merged.map((r) => [r.id, r])).values(),
      // );

      setResources(resources);
    } catch (error) {
      console.error("Failed to fetch resources", error);
    } finally {
      setLoading(false);
    }
  };

  const getAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await SpecificAPI.getAllUsers();
      setAllUsers(response || []);
    } catch (err) {
      toast.error("Failed to fetch staff members.");
      console.error("Error fetching staff members:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    filterResources();
  }, [searchTerm, resources]);

  const filterResources = () => {
    let filtered = resources;
    if (searchTerm) {
      filtered = filtered.filter((res) =>
        res.originalName.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredItems(filtered);
  };

  const handleResourceUpload = async (formData) => {
    try {
      setLoading(true);
      const res = await SpecificAPI.createResources(formData);
      if (res?.status === 200 || res?.status === 201) {
        toast.success("Resources uploaded successfully!");
        await getResources(appUser.id);
      }
      return res;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setItemName(item.originalName || "");
    setShowDeleteModal(true);
  };

  const handleView = (item) => {
    setItemToView(item);
    setShowViewModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      await SpecificAPI.deleteResources(
        itemToDelete.id,
        userData?.selected_school_id,
      );

      // await SpecificAPI.updateResources(
      //   userData?.selected_school_id,
      //   itemToDelete.id,
      //   {
      //     enabled: false,
      //   },
      // );

      setResources((prev) => prev.filter((ev) => ev.id !== itemToDelete.id));

      toast.success("Resource deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete resource. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setUploadError("");
  };

  return (
    <div className="min-h-screen p-8 relative">
      {(loading || loadingUsers) && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-50">
          <ClipLoader size={50} color="var(--primary-icon)" />
        </div>
      )}
      <div className="w-full mx-auto">
        {/* Header */}
        <ManagementHeader
          title={localization.manage_resources || "Manage Resources"}
          subtitle={
            localization.view_upload_delete_your_resources ||
            "View, upload, and delete your resources"
          }
          buttonText={localization.add_resource || "Add Resource"}
          onButtonClick={() => setShowModal(true)}
          showBack={true}
          backTo={
            appUser?.role != "root"
              ? `/prompt-categories/${moduleId}/${departmentId}`
              : "/schools"
          }
          backLabel="Back to organization"
        />
        <TooltipProvider delayDuration={100}>
          <ResourceTable
            resources={resources}
            onView={handleView}
            onDelete={handleDelete}
            allUsers={allUsers}
            isRoot={appUser?.role === "root"}
            userId={appUser?.id}
          />
        </TooltipProvider>

        {/* Upload Modal */}
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          centered
          size="lg"
          backdrop="static"
          className="themed-modal addEditModal"
        >
          <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
            <ResourceUpload
              onResourceUpload={handleResourceUpload}
              onBackClick={handleCloseModal}
              appUser={appUser}
              userData={userData}
              isModal={true}
              uploadError={uploadError}
              setUploadError={setUploadError}
              loading={loading}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              {localization.cancel || "Cancel"}
            </Button>
          </Modal.Footer>
        </Modal>
        <Modal
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          centered
          size="xl"
          backdrop="static"
          className="themed-modal addEditModal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {itemToView?.originalName || "View Resource"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "90vh", overflowY: "auto" }}>
            {itemToView && (
              <div className="w-full h-full flex items-center justify-center">
                {itemToView.type === "image" ? (
                  <img
                    src={itemToView.url || "/placeholder.svg"}
                    alt={itemToView.originalName}
                    className="max-w-full max-h-[80vh] object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ResourcePreview resource={itemToView} />
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
      <DeleteModal
        show={showDeleteModal}
        itemName={itemName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
}
